import React, { useEffect, useRef, useMemo, memo } from "react";
import {
    APIProvider,
    Map as GoogleMap,
    AdvancedMarker,
    useApiIsLoaded,
    useMap
} from "@vis.gl/react-google-maps";

const mapContainerStyle = {
    width: "100%",
    height: "600px",
    borderRadius: "0.5rem",
};

const defaultCenter = { lat: 39.8283, lng: -98.5795 };

const LeadMarker = memo(({ lead, onClick }) => {
    const lat = parseFloat(lead.latitude);
    const lng = parseFloat(lead.longitude);

    const position = useMemo(() => ({ lat, lng }), [lat, lng]);

    return (
        <AdvancedMarker
            position={position}
            title={lead.property_address}
            onClick={() => onClick(lead)}
        >
            <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#3b82f6',
                border: '2px solid white',
                borderRadius: '50%',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }} />
        </AdvancedMarker>
    );
}, (prevProps, nextProps) => {
    const prevLat = parseFloat(prevProps.lead.latitude);
    const nextLat = parseFloat(nextProps.lead.latitude);
    const prevLng = parseFloat(prevProps.lead.longitude);
    const nextLng = parseFloat(nextProps.lead.longitude);

    return (
        // CHANGE 4: Force String comparison
        String(prevProps.lead.pid) === String(nextProps.lead.pid) &&
        prevLat === nextLat &&
        prevLng === nextLng
    );
});

const SellerLeadsMap = ({ filteredLeads, handleRowClick, setDummyLeads, dummyLeads }) => {
    const isApiLoaded = useApiIsLoaded();
    const mapInstance = useMap("da37f39de34298d9");

    const geocodedLeadsRef = useRef(new Set());
    const currentExecutionRef = useRef(null);

    const leadsRef = useRef(filteredLeads);
    const setterRef = useRef(setDummyLeads);

const allLeadsRef = useRef(dummyLeads);
    useEffect(() => {
        allLeadsRef.current = dummyLeads;
    }, [dummyLeads]);

    const leadsCount = filteredLeads?.length || 0;

    useEffect(() => {
        let isMounted = true;
        const executionId = Math.random();
        currentExecutionRef.current = executionId;

        const geocodeMissingLeads = async () => {
            const currentLeads = leadsRef.current;
            if (!isMounted || !isApiLoaded || !currentLeads?.length) return;

            const leadsToGeocode = currentLeads.filter((l) => {
                const hasCoords = l.latitude && l.longitude && parseFloat(l.latitude) !== 0;
                return l.property_address && !hasCoords && !geocodedLeadsRef.current.has(l.pid);
            });

            if (leadsToGeocode.length === 0) return;

            const geocoder = new window.google.maps.Geocoder();
            const batchResults = [];

            for (const lead of leadsToGeocode) {
                if (!isMounted || currentExecutionRef.current !== executionId) break;

                // FIXED: Safety check for undefined PIDs
                if (!lead.pid) {
                    console.warn("Lead missing PID encountered during geocoding.", lead);
                    continue;
                }

                const address = `${lead.property_address}, ${lead.property_city}, ${lead.property_state} ${lead.property_zip}`;

                try {
                    const result = await new Promise((resolve, reject) => {
                        geocoder.geocode({ address }, (results, status) => {
                            if (status === "OK" && results[0]) resolve(results[0]);
                            else reject(new Error(status));
                        });
                    });

                    if (result?.geometry?.location) {
                        batchResults.push({
                            pid: lead.pid,
                            lat: result.geometry.location.lat(),
                            lng: result.geometry.location.lng()
                        });
                    }
                } catch (err) {
                    console.error(`Geocoding failed for ${lead.pid}:`, err.message);
                } finally {
                    geocodedLeadsRef.current.add(lead.pid);
                }
                await new Promise((r) => setTimeout(r, 200));
            }

if (batchResults.length > 0 && isMounted && currentExecutionRef.current === executionId) {
                // Bypass functional setter bug using the absolute truth ref
                const updateLookup = new Map(batchResults.map(res => [String(res.pid), res]));
                const currentAllLeads = allLeadsRef.current || [];

                const updatedLeads = currentAllLeads.map((lead) => {
                    const update = updateLookup.get(String(lead.pid));
                    return update ? { ...lead, latitude: update.lat, longitude: update.lng } : lead;
                });

                // Pass the raw array
                setterRef.current(updatedLeads);
            }
}


        geocodeMissingLeads();

        return () => {
            isMounted = false;
            currentExecutionRef.current = null;
        };
    }, [leadsCount, isApiLoaded]);

    // FIXED: Truncate precision to block jitter loops
    const coordinatesHash = useMemo(() => {
        return (filteredLeads || [])
            .filter(l => l.latitude && l.longitude && parseFloat(l.latitude) !== 0)
            .map(l => `${l.pid}:${parseFloat(l.latitude).toFixed(5)},${parseFloat(l.longitude).toFixed(5)}`)
            .join('|');
    }, [filteredLeads]);

    useEffect(() => {
        if (!mapInstance || !window.google || !coordinatesHash) return;

        const validLeads = leadsRef.current.filter((l) => {
            const lat = parseFloat(l.latitude);
            const lng = parseFloat(l.longitude);
            return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        });

        if (validLeads.length === 0) {
            mapInstance.setCenter(defaultCenter);
            mapInstance.setZoom(4);
            return;
        }

        try {
            const bounds = new window.google.maps.LatLngBounds();
            validLeads.forEach((lead) => {
                bounds.extend({
                    lat: parseFloat(lead.latitude),
                    lng: parseFloat(lead.longitude),
                });
            });

            if (validLeads.length === 1) {
                mapInstance.setCenter({
                    lat: parseFloat(validLeads[0].latitude),
                    lng: parseFloat(validLeads[0].longitude)
                });
                mapInstance.setZoom(15);
            } else {
                mapInstance.fitBounds(bounds);
            }
        } catch (err) {
            console.error("Map bounds error:", err);
        }
    }, [mapInstance, coordinatesHash]);

    // FIXED: Rely on the hash, not the array reference
    const mapMarkers = useMemo(() => {
        return (filteredLeads || []).map((lead) => {
            const lat = parseFloat(lead.latitude);
            const lng = parseFloat(lead.longitude);
            const isValid = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0 && lead.pid;

            if (!isValid) return null;

            return (
                <LeadMarker
                    key={`marker-${lead.pid}`}
                    lead={lead}
                    onClick={handleRowClick}
                />
            );
        });
    }, [filteredLeads, handleRowClick]);

    return (
        <div className="p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            <GoogleMap
                style={mapContainerStyle}
                defaultCenter={defaultCenter}
                defaultZoom={4}
                mapId="da37f39de34298d9"
            >
                {mapMarkers}
            </GoogleMap>
        </div>
    );
};

const SellerPagesMapWrapper = (props) => (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
        <SellerLeadsMap {...props} />
    </APIProvider>
);

export default SellerPagesMapWrapper;